import * as React from 'react';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import {
    TooltipComponent,
    LegendComponent,
    GridComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import type {
    PieSeriesOption,
    TooltipComponentOption,
    LegendComponentOption,
    GridComponentOption,
} from 'echarts';
import { Card, CardContent, Grid, Box, Skeleton } from '@mui/material';
import DashboardPanelHeader from '../components/DashboardPanelHeader';
import type { DashboardExportParams } from '../utils/dashboardExportApi';

type ECOption = echarts.ComposeOption<
    | PieSeriesOption
    | TooltipComponentOption
    | LegendComponentOption
    | GridComponentOption
>;

echarts.use([PieChart, TooltipComponent, LegendComponent, GridComponent, CanvasRenderer]);

type BreakdownItem = { label: string; value: number };

interface BreakdownData {
    device_type?: BreakdownItem[];
    browser?: BreakdownItem[];
    platform?: BreakdownItem[];
    language?: BreakdownItem[];
    timezone?: BreakdownItem[];
    country?: BreakdownItem[];
    region?: BreakdownItem[];
    city?: BreakdownItem[];
    screen_size?: BreakdownItem[];
}

interface VisitorBreakdownChartsProps {
    data?: BreakdownData | null;
    loading?: boolean;
    exportParams?: Omit<DashboardExportParams, 'panel'>;
}

const chartDefs: { key: keyof BreakdownData; title: string }[] = [
    { key: 'device_type', title: 'Devices' },
    { key: 'browser', title: 'Browsers' },
    { key: 'platform', title: 'Platform' },
    { key: 'country', title: 'Countries' },
    { key: 'region', title: 'Regions' },
    { key: 'city', title: 'Cities (Top 15)' },
    { key: 'language', title: 'Languages' },
    { key: 'timezone', title: 'Timezones' },
    { key: 'screen_size', title: 'Screen Sizes' },
];

const VisitorBreakdownCharts: React.FC<VisitorBreakdownChartsProps> = ({ data, loading, exportParams }) => {
    const refs = React.useRef<Record<string, HTMLDivElement | null>>({});
    const instances = React.useRef<Record<string, echarts.ECharts | null>>({});

    React.useEffect(() => {
        if (!data || loading) return;

        chartDefs.forEach(({ key }) => {
            const el = refs.current[key];
            if (!el) return;

            // init or reuse
            const instance = instances.current[key] || echarts.init(el);
            instances.current[key] = instance;

            const items = data[key] || [];
            const option: ECOption = {
                tooltip: {
                    trigger: 'item',
                    formatter: '{b}: {c} ({d}%)',
                },
                legend: {
                    type: 'scroll',
                    bottom: 0,
                },
                series: [
                    {
                        name: key,
                        type: 'pie',
                        radius: ['35%', '70%'],
                        avoidLabelOverlap: true,
                        itemStyle: {
                            borderRadius: 4,
                            borderColor: '#fff',
                            borderWidth: 1,
                        },
                        label: {
                            show: false,
                        },
                        labelLine: {
                            show: false,
                        },
                        data: items.map(item => ({ name: item.label, value: item.value })),
                    },
                ],
            };

            instance.setOption(option);
        });

        const handleResize = () => {
            Object.values(instances.current).forEach(inst => inst?.resize());
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            Object.keys(instances.current).forEach(key => {
                instances.current[key]?.dispose();
                instances.current[key] = null;
            });
        };
    }, [data, loading]);

    const hasData = data && Object.values(data).some(arr => (arr || []).length > 0);

    return (
        <Card sx={{ marginTop: 3 }}>
            <DashboardPanelHeader
                title="Visitor Segments"
                subheader="New vs returning applies in trends; here we break down devices, geo, and tech."
                exportFilename="visitor-segments"
                exportPanel="visitors_detail"
                exportParams={exportParams}
            />
            <CardContent>
                {loading ? (
                    <Skeleton height={420} />
                ) : !hasData ? (
                    <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <p>No visitor segment data for the selected range</p>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {chartDefs.map(({ key, title }) => (
                            <Grid item xs={12} sm={6} md={4} key={key}>
                                <Box sx={{ height: 240 }}>
                                    <Box sx={{ fontWeight: 600, mb: 1 }}>{title}</Box>
                                    <div
                                        ref={el => {
                                            refs.current[key] = el;
                                        }}
                                        style={{ width: '100%', height: '200px' }}
                                    />
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </CardContent>
        </Card>
    );
};

export default VisitorBreakdownCharts;


